import React, { useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Paginator } from "primereact/paginator";
import { useTranslation } from "react-i18next";
import { AdvanceFilter } from "../../M_Components/M_Table/advanceFilter/AdvanceFilter";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import TableHeader from "../tableHelpers/TableHeader";
import ColumnMenu from "../tableHelpers/ColumnMenu";
import ViewsMenu from "../tableHelpers/ViewMenu";
import { MCheckBox } from "src/M_Components";
import {
  getCellColor,
  headerCellStyle,
  cellStyle as tdStyle,
} from "../tableHelpers/TableStyleUtils";
import { useTableFilters } from "../tableHelpers/useTableFilters";
import "./ViewLaneTable.css";

//region Stable default values

/**
 * Stable empty defaults used instead of defaultProps.
 *
 * Important:
 * - avoids the React warning about defaultProps on function components;
 * - avoids creating new array/map/set instances on every render.
 */
const EMPTY_ARRAY = [];
const EMPTY_MAP = new Map();
const EMPTY_SET = new Set();

//endregion

//region Quote helpers

/**
 * Reads UnitPrice from a quote.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|null} UnitPrice or null.
 */
const getUnitPriceValue = (quote) => {
  if (!quote) return null;

  const value = Number(quote.UnitPrice ?? quote.unitPrice);
  return Number.isFinite(value) ? value : null;
};

/**
 * Reads BidValue from a quote.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|null} BidValue or null.
 */
const getBidValue = (quote) => {
  if (!quote) return null;

  const value = Number(
    quote.BidValue ?? quote.bidValue ?? quote.Bid ?? quote.bid
  );

  return Number.isFinite(value) ? value : null;
};

/**
 * Reads Loads from a quote.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|null} Loads or null.
 */
const getLoadsValue = (quote) => {
  if (!quote) return null;

  const value = Number(quote.Loads ?? quote.loads);
  return Number.isFinite(value) ? value : null;
};

/**
 * Reads Estimated Transit Days from a quote.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|null} Estimated transit days or null.
 */
const getTransitValue = (quote) => {
  if (!quote) return null;

  const value = Number(
    quote.EstimatedTransitDays ??
    quote.estimatedTransitDays ??
    quote.TransitDays ??
    quote.transitDays
  );

  return Number.isFinite(value) ? value : null;
};

/**
 * Reads the metric value of a quote for the currently selected metric.
 *
 * Supported metrics:
 * - unit    => UnitPrice
 * - bid     => BidValue
 * - loads   => Loads
 * - transit => EstimatedTransitDays
 *
 * @param {"unit"|"bid"|"loads"|"transit"} metricKey - Active metric.
 * @param {object} quote - Quote DTO.
 * @returns {number|null} Metric value or null.
 */
const getQuoteMetricValue = (metricKey, quote) => {
  switch (metricKey) {
    case "unit":
      return getUnitPriceValue(quote);

    case "bid":
      return getBidValue(quote);

    case "loads":
      return getLoadsValue(quote);

    case "transit":
      return getTransitValue(quote);

    default:
      return null;
  }
};

/**
 * Reads the quote identifier.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|string|null} Quote id.
 */
const getQuoteId = (quote) =>
  quote?.QuoteId ?? quote?.quoteId ?? quote?.Id ?? quote?.id ?? null;

/**
 * Reads the phase identifier from a quote.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|null} Phase id.
 */
const getQuotePhaseId = (quote) =>
  quote?.TenderPhaseId ??
  quote?.tenderPhaseId ??
  quote?.TenderPhase?.Id ??
  quote?.TenderPhase?.id ??
  quote?.tenderPhase?.Id ??
  quote?.tenderPhase?.id ??
  null;

/**
 * Reads the accepted flag from a quote.
 *
 * @param {object} quote - Quote DTO.
 * @returns {boolean} Accepted flag.
 */
const getQuoteAccepted = (quote) =>
  quote?.IsAccepted ?? quote?.isAccepted ?? false;

/**
 * Reads the "last phase" information from a quote when available.
 *
 * @param {object} quote - Quote DTO.
 * @returns {boolean} True when the quote belongs to the last phase.
 */
const getQuoteIsLastPhase = (quote) => {
  if (!quote) return false;

  if (quote.IsLastPhase !== undefined) return !!quote.IsLastPhase;
  if (quote.isLastPhase !== undefined) return !!quote.isLastPhase;

  const tenderPhase = quote.TenderPhase ?? quote.tenderPhase ?? null;

  if (tenderPhase?.IsLastPhase !== undefined) return !!tenderPhase.IsLastPhase;
  if (tenderPhase?.isLastPhase !== undefined)
    return !!tenderPhase.isLastPhase;

  return false;
};

/**
 * Sorts quotes from best to worst according to the selected metric.
 *
 * Important:
 * - this sorting is purely metric-driven;
 * - it does NOT privilege accepted quotes;
 * - it does NOT privilege suggested quotes;
 * - it does NOT privilege any carrier;
 * - ties fall back to QuoteId to keep the output stable.
 *
 * Ordering rules:
 * - unit    => cheaper first
 * - bid     => cheaper first
 * - transit => lower ETD first
 * - loads   => higher loads first
 *
 * @param {"unit"|"bid"|"loads"|"transit"} metricKey - Active metric.
 * @param {object} a - First quote.
 * @param {object} b - Second quote.
 * @returns {number} Sort comparator.
 */
const compareQuotesByMetric = (metricKey, a, b) => {
  const aValue = getQuoteMetricValue(metricKey, a);
  const bValue = getQuoteMetricValue(metricKey, b);

  const aHasValue = aValue != null;
  const bHasValue = bValue != null;

  /**
   * Quotes with real metric values must appear before quotes without a value.
   */
  if (aHasValue && !bHasValue) return -1;
  if (!aHasValue && bHasValue) return 1;

  if (aHasValue && bHasValue) {
    if (metricKey === "loads") {
      if (aValue !== bValue) return bValue - aValue;
    } else {
      if (aValue !== bValue) return aValue - bValue;
    }
  }

  /**
   * Stable tie-break by QuoteId.
   */
  const aId = getQuoteId(a);
  const bId = getQuoteId(b);

  if (aId == null && bId == null) return 0;
  if (aId == null) return 1;
  if (bId == null) return -1;

  return String(aId).localeCompare(String(bId), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

//endregion

//region Solution helpers

/**
 * Reads the QuoteId from one solution award row.
 *
 * @param {object} award - Suggested award DTO.
 * @returns {number|null} Quote id.
 */
const getAwardQuoteId = (award) => {
  if (!award) return null;

  return (
    award.TenderQuoteId ??
    award.tenderQuoteId ??
    award.QuoteId ??
    award.quoteId ??
    award.Id ??
    award.id ??
    null
  );
};

//endregion

//region Lane helpers

/**
 * Reads the lane identifier.
 *
 * @param {object} lane - Lane DTO.
 * @returns {number|null} Lane id.
 */
const getLaneId = (lane) =>
  lane?.LaneId ?? lane?.laneId ?? lane?.Id ?? lane?.id ?? null;

/**
 * Reads the origin label of a lane.
 *
 * @param {object} lane - Lane DTO.
 * @returns {string} Origin label.
 */
const getLaneOrigin = (lane) =>
  lane?.LaneSource ??
  lane?.laneSource ??
  lane?.Origin ??
  lane?.origin ??
  lane?.sourceLocation ??
  lane?.SourceLocation ??
  "";

/**
 * Reads the destination label of a lane.
 *
 * @param {object} lane - Lane DTO.
 * @returns {string} Destination label.
 */
const getLaneDestination = (lane) =>
  lane?.LaneDestination ??
  lane?.laneDestination ??
  lane?.Destination ??
  lane?.destination ??
  lane?.destinationLocation ??
  lane?.DestinationLocation ??
  "";

/**
 * Reads whether a lane is active.
 *
 * @param {object} lane - Lane DTO.
 * @returns {boolean} True when active.
 */
const isLaneActive = (lane) => {
  if (!lane) return false;

  if (lane.IsActive !== undefined) return !!lane.IsActive;
  if (lane.isActive !== undefined) return !!lane.isActive;

  const inactiveFlag = lane.Inactive ?? lane.inactive;
  if (inactiveFlag !== undefined) return !inactiveFlag;

  return true;
};

//endregion

//region Generic UI helpers

/**
 * Returns the alternating base background color for a lane group.
 *
 * Important:
 * - the alternation is lane-based, not quote-based;
 * - all quote rows inside the same lane group use the same base background.
 *
 * @param {number} laneIndex - Index of the lane inside the paged lane collection.
 * @returns {string} CSS color variable.
 */
const getLaneBaseBackground = (laneIndex) =>
  laneIndex % 2 === 0 ? "var(--color-white)" : "var(--color-grey-light)";

/**
 * Returns the visible quote sub-line separator color for the lane background.
 *
 * Important:
 * - white lane groups use grey-light separators;
 * - grey-light lane groups use white separators;
 * - this keeps quote sub-lines visible in both alternating backgrounds.
 *
 * @param {string} rowBaseBackground - Lane base background.
 * @returns {string} CSS color variable.
 */
const getLaneSubLineColor = (rowBaseBackground) =>
  rowBaseBackground === "var(--color-grey-light)"
    ? "var(--color-white)"
    : "var(--color-grey-light)";

/**
 * Tries to resolve the Tender last phase id from tenderInfo.
 *
 * @param {object} tenderInfo - Tender payload.
 * @returns {number|null} Last phase id.
 */
const resolveLastPhaseIdFromTenderInfo = (tenderInfo) => {
  const phases =
    tenderInfo?.Phases ??
    tenderInfo?.phases ??
    tenderInfo?.TenderPhases ??
    tenderInfo?.tenderPhases ??
    [];

  if (!Array.isArray(phases) || phases.length === 0) return null;

  const explicitLast = phases.find(
    (phase) => (phase?.IsLastPhase ?? phase?.isLastPhase) === true
  );
  if (explicitLast?.Id != null) return explicitLast.Id;
  if (explicitLast?.id != null) return explicitLast.id;

  const parentIds = phases
    .map(
      (phase) => phase?.LastTenderPhaseId ?? phase?.lastTenderPhaseId ?? null
    )
    .filter((value) => value != null);

  const last =
    phases.find((phase) => {
      const phaseId = phase?.Id ?? phase?.id ?? null;
      return phaseId != null && !parentIds.includes(phaseId);
    }) || phases[phases.length - 1];

  return last?.Id ?? last?.id ?? null;
};

/**
 * Returns the translated label for the current best metric column.
 *
 * Supported metrics:
 * - unit
 * - bid
 * - loads
 * - transit
 *
 * @param {"unit"|"bid"|"loads"|"transit"} metricKey - Active metric.
 * @param {Function} t - Translation function.
 * @returns {string} Header label.
 */
const getBestMetricHeader = (metricKey, t) => {
  switch (metricKey) {
    case "unit":
      return t("BestUnitValue") || "Best Unit Value";

    case "bid":
      return t("BestBidValue") || "Best Bid Value";

    case "loads":
      return t("MoreLoads") || "More Loads";

    case "transit":
      return t("BestEstimatedTransitDays") || "Best Estimated Transit Days";

    default:
      return t("BestMetric") || "Best Metric";
  }
};

/**
 * Returns the translated export filename suffix according to the current mode.
 *
 * @param {"accepted"|"total"} laneMode - Current lane mode.
 * @returns {string} File suffix.
 */
const getExportFileSuffix = (laneMode) =>
  laneMode === "total" ? "TotalByLane" : "AcceptedByLane";

/**
 * Reads the Tender currency value regardless of casing/shape.
 *
 * Important:
 * - in TenderPhase mode, the currency still belongs to the parent Tender;
 * - this component already receives tenderInfo from the parent page.
 *
 * Supported shapes:
 * - tenderInfo.Currency = "$"
 * - tenderInfo.currency = "EUR"
 * - tenderInfo.CurrencyCode = "USD"
 * - tenderInfo.Currency = { Symbol, Code, Name }
 *
 * @param {object} tenderInfo - Tender DTO.
 * @returns {string} Printable currency suffix.
 */
const getTenderCurrencyLabel = (tenderInfo) => {
  const rawCurrency =
    tenderInfo?.Currency ??
    tenderInfo?.currency ??
    tenderInfo?.CurrencySymbol ??
    tenderInfo?.currencySymbol ??
    tenderInfo?.CurrencyCode ??
    tenderInfo?.currencyCode ??
    tenderInfo?.CurrencyName ??
    tenderInfo?.currencyName ??
    "";

  if (rawCurrency && typeof rawCurrency === "object") {
    return String(
      rawCurrency.Symbol ??
      rawCurrency.symbol ??
      rawCurrency.Code ??
      rawCurrency.code ??
      rawCurrency.Name ??
      rawCurrency.name ??
      ""
    ).trim();
  }

  return String(rawCurrency ?? "").trim();
};

/**
 * Checks whether a metric is monetary.
 *
 * @param {"unit"|"bid"|"loads"|"transit"} metricKey - Current metric.
 * @returns {boolean} True when currency must be shown.
 */
const isMoneyMetric = (metricKey) => metricKey === "unit" || metricKey === "bid";

/**
 * Formats a numeric value with the current locale.
 *
 * @param {number} value - Numeric value.
 * @param {string|undefined} locale - UI locale.
 * @returns {string} Formatted number.
 */
const formatNumber = (value, locale) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));

const THICK_BORDER = "2px";
const THIN_BORDER = "1px";
const NO_BORDER = "0px";

/**
 * Returns the border hierarchy for quote-level cells inside one lane group.
 *
 * Important:
 * - vertical borders must always stay thick;
 * - the separator between quotes inside the SAME lane must be thin;
 * - the boundary between DIFFERENT lanes must stay thick.
 *
 * With border-collapse this is achieved by:
 * - first quote of the lane:
 *      * top border = thick
 * - inner quotes:
 *      * top border = thin
 * - last quote of the lane:
 *      * bottom border = thick
 * - non-last quotes:
 *      * bottom border = none
 *
 * This makes the shared horizontal border between quotes inside the same lane
 * appear thin, while the shared boundary between the last quote of one lane
 * and the first quote of the next lane remains thick.
 *
 * @param {number} quoteIndex - Zero-based quote index inside the lane group.
 * @param {number} totalQuotes - Total visible quotes in the lane group.
 * @returns {object} Border width overrides for one quote-level cell.
 */
const getQuoteCellBorderStyle = (quoteIndex, totalQuotes) => {
  const isFirst = quoteIndex === 0;
  const isLast = quoteIndex === totalQuotes - 1;

  return {
    borderStyle: "solid",
    borderColor: "var(--color-black)",

    // Vertical borders are always thick.
    borderLeftWidth: THICK_BORDER,
    borderRightWidth: THICK_BORDER,

    // Top horizontal border:
    // - first quote in the lane => thick
    // - inner quotes => thin
    borderTopWidth: isFirst ? THICK_BORDER : THIN_BORDER,

    // Bottom horizontal border:
    // - last quote in the lane => thick
    // - all other quotes => none
    borderBottomWidth: isLast ? THICK_BORDER : NO_BORDER,

    // Explicitly disable visual shadow-based separators.
    boxShadow: "none",
  };
};

//endregion

/**
 * ViewLaneTable
 *
 * This table renders one lane as a group.
 * A lane may contain multiple visible quotes, one row per quote.
 *
 * Supported modes:
 * - accepted: only accepted quotes are rendered
 * - total: all active quotes are rendered
 *
 * Important semantics:
 * - this table is Tender-wide for quotes;
 * - currentPhaseId is context for the active solution target only,
 *   not a FE filter over the quote dataset;
 * - supported comparison metrics are now:
 *      1) unit
 *      2) bid
 *      3) loads
 *      4) transit
 * - quotes inside each lane are always sorted from best to worst according to
 *   the CURRENT metric, regardless of acceptance state.
 *
 * Important visual rule:
 * - the thin separator logic is applied inline ONLY to quote-level repeating cells;
 * - lane-level rowSpan cells keep the normal borders;
 * - no special quote-border classes are used here so old CSS rules cannot interfere.
 */
export default function ViewLaneTable({
  tenderInfo = {},
  acceptedQuotes = EMPTY_ARRAY,
  allQuotes = EMPTY_ARRAY,
  lanes = EMPTY_ARRAY,
  carriers = EMPTY_ARRAY,
  onRefresh = null,
  metricKey = "unit",
  laneMode = "accepted",
  isFinished = false,
  isSpotTender = false,
  currentPhaseId = null,
  laneBestMap = EMPTY_MAP,
  tenderTargetLoads = null,
  laneRemainingLoadsByLaneId = EMPTY_MAP,
  acceptBusyIds = EMPTY_SET,
  onSetQuoteAccepted = null,
  showAcceptedColumn = false,
  showSuggestedColumn = false,
  analysisSolutionReady = false,
  selectedSolutionId = null,
  selectedSolution = null,
  selectedSolutionAwards = EMPTY_ARRAY,
  selectedSolutionSuggestedQuoteIds = EMPTY_ARRAY,
  selectedSolutionAwardsByLaneId = EMPTY_MAP,
}) {
  const { t, i18n } = useTranslation();

  //region Currency formatting

  /**
   * Currency inherited from the Tender parent.
   *
   * Important:
   * - in TenderPhase context, this must still be the parent Tender currency.
   */
  const currencyLabel = useMemo(
    () => getTenderCurrencyLabel(tenderInfo),
    [tenderInfo]
  );

  const currentLocale = i18n?.resolvedLanguage || i18n?.language || undefined;

  /**
   * Formats a normal non-currency value.
   *
   * @param {number|null} value - Numeric value.
   * @returns {string} Display text.
   */
  const formatPlainValue = useCallback(
    (value) => {
      if (value == null) return "-";

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return "-";

      return formatNumber(numericValue, currentLocale);
    },
    [currentLocale]
  );

  /**
   * Formats a currency value.
   *
   * Example:
   * - value: 200
   * - currencyLabel: "$"
   * - result: "200 $"
   *
   * @param {number|null} value - Numeric value.
   * @returns {string} Display text.
   */
  const formatCurrencyValue = useCallback(
    (value) => {
      if (value == null) return "-";

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return "-";

      const formatted = formatNumber(numericValue, currentLocale);

      return currencyLabel ? `${formatted} ${currencyLabel}` : formatted;
    },
    [currencyLabel, currentLocale]
  );

  /**
   * Formats a value according to the current metric semantic.
   *
   * @param {number|null} value - Numeric value.
   * @param {"unit"|"bid"|"loads"|"transit"} metric - Metric type.
   * @returns {string} Display text.
   */
  const formatMetricValue = useCallback(
    (value, metric) => {
      if (isMoneyMetric(metric)) {
        return formatCurrencyValue(value);
      }

      return formatPlainValue(value);
    },
    [formatCurrencyValue, formatPlainValue]
  );

  //endregion

  //region Derived rendering flags

  /**
   * In Non-Spot mode the page uses currentPhaseId to decide which phase's
   * solutions are currently loaded/selected.
   *
   * Important:
   * - this does NOT mean the table filters quotes by phase;
   * - it only means suggestion highlighting depends on the current solution target.
   */
  const hasSolutionTargetContext = useMemo(() => {
    return isSpotTender || currentPhaseId != null;
  }, [isSpotTender, currentPhaseId]);

  /**
   * Suggested is only relevant when:
   * - the workflow wants the column;
   * - analysis results are ready;
   * - there is a solution target context;
   * - there is actually a selected solution.
   */
  const shouldRenderSuggestedColumn = useMemo(
    () =>
      Boolean(showSuggestedColumn) &&
      Boolean(analysisSolutionReady) &&
      Boolean(hasSolutionTargetContext) &&
      selectedSolutionId != null,
    [
      showSuggestedColumn,
      analysisSolutionReady,
      hasSolutionTargetContext,
      selectedSolutionId,
    ]
  );

  /**
   * Accepted is only relevant when the workflow wants the column.
   */
  const shouldRenderAcceptedColumn = useMemo(
    () => Boolean(showAcceptedColumn),
    [showAcceptedColumn]
  );

  //endregion

  //region Last phase resolution

  /**
   * Used to decide whether quote acceptance can be changed in Non-Spot mode.
   *
   * This rule remains independent from the phase currently chosen in the page
   * for solution visualization.
   */
  const lastPhaseId = useMemo(
    () => (isSpotTender ? null : resolveLastPhaseIdFromTenderInfo(tenderInfo)),
    [isSpotTender, tenderInfo]
  );

  //endregion

  //region Visible quote source

  /**
   * Chooses the visible source based on the lane mode.
   *
   * accepted -> acceptedQuotes
   * total    -> allQuotes
   */
  const sourceQuotes = useMemo(() => {
    return laneMode === "total" ? allQuotes || [] : acceptedQuotes || [];
  }, [laneMode, allQuotes, acceptedQuotes]);

  /**
   * Keeps only active quotes.
   *
   * Important:
   * - this table is Tender-wide;
   * - therefore it MUST NOT filter quotes by currentPhaseId;
   * - currentPhaseId is only used by the page to load the active solution set.
   */
  const visibleQuotes = useMemo(() => {
    return sourceQuotes.filter((quote) => {
      const inactiveFlag = quote?.Inactive ?? quote?.inactive;
      return inactiveFlag === undefined || inactiveFlag === false;
    });
  }, [sourceQuotes]);

  //endregion

  //region Fast lookup maps

  /**
   * CarrierId -> CarrierName
   */
  const carrierNameById = useMemo(() => {
    const map = new Map();

    for (const carrier of carriers || []) {
      const carrierId =
        carrier?.CarrierId ??
        carrier?.Id ??
        carrier?.carrierId ??
        carrier?.id ??
        null;

      if (carrierId == null) continue;

      const carrierName =
        carrier?.CarrierName ??
        carrier?.Name ??
        carrier?.carrierName ??
        carrier?.name ??
        null;

      if (carrierName) {
        map.set(Number(carrierId), carrierName);
      }
    }

    return map;
  }, [carriers]);

  /**
   * LaneId -> Quote[]
   *
   * Important:
   * - quotes are grouped by lane;
   * - inside each lane group they are ALWAYS sorted by the current metric;
   * - the sort is purely metric-driven and does not separate accepted/unaccepted.
   */
  const quotesByLaneId = useMemo(() => {
    const map = new Map();

    for (const quote of visibleQuotes || []) {
      const laneIdRaw = quote?.LaneId ?? quote?.laneId ?? quote?.LaneID ?? null;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;

      if (!Number.isFinite(laneId)) continue;

      if (!map.has(laneId)) {
        map.set(laneId, []);
      }

      map.get(laneId).push(quote);
    }

    for (const [laneId, laneQuotes] of map.entries()) {
      const sorted = [...laneQuotes].sort((a, b) =>
        compareQuotesByMetric(metricKey, a, b)
      );
      map.set(laneId, sorted);
    }

    return map;
  }, [visibleQuotes, metricKey]);

  /**
   * Builds a stable set of suggested QuoteIds from the current solution.
   *
   * Source priority:
   * 1) explicit selectedSolutionSuggestedQuoteIds
   * 2) selectedSolutionAwards
   * 3) selectedSolution.SuggestedAwards
   *
   * Important:
   * - these awards are already expected to come target-scoped from the page;
   * - this component does not heavy-filter raw multi-phase solution collections.
   */
  const selectedSolutionSuggestedQuoteIdSet = useMemo(() => {
    const explicitIds = Array.isArray(selectedSolutionSuggestedQuoteIds)
      ? selectedSolutionSuggestedQuoteIds
      : [];

    if (explicitIds.length > 0) {
      return new Set(
        explicitIds
          .map((quoteId) => (quoteId != null ? Number(quoteId) : null))
          .filter((quoteId) => Number.isFinite(quoteId))
      );
    }

    const awardsFromProps = Array.isArray(selectedSolutionAwards)
      ? selectedSolutionAwards
      : [];

    const awardsFromSolution = Array.isArray(selectedSolution?.SuggestedAwards)
      ? selectedSolution.SuggestedAwards
      : Array.isArray(selectedSolution?.suggestedAwards)
        ? selectedSolution.suggestedAwards
        : [];

    const awardsSource =
      awardsFromProps.length > 0 ? awardsFromProps : awardsFromSolution;

    return new Set(
      awardsSource
        .map((award) => getAwardQuoteId(award))
        .map((quoteId) => (quoteId != null ? Number(quoteId) : null))
        .filter((quoteId) => Number.isFinite(quoteId))
    );
  }, [
    selectedSolutionSuggestedQuoteIds,
    selectedSolutionAwards,
    selectedSolution,
  ]);

  /**
   * Optional lane-scoped award map fallback.
   * This can be useful when the parent already grouped awards by lane.
   */
  const selectedSolutionLaneAwardMap = useMemo(() => {
    if (!(selectedSolutionAwardsByLaneId instanceof Map)) {
      return null;
    }

    return selectedSolutionAwardsByLaneId;
  }, [selectedSolutionAwardsByLaneId]);

  //endregion

  //region Pagination and saved filters

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const [anchorAF, setAnchorAF] = useState(null);
  const [anchorEls, setAnchorEls] = useState(null);
  const [anchorV, setAnchorV] = useState(null);

  const {
    metaPair,
    views,
    addMetaPair,
    deleteFilter,
    deleteAllFilters,
    submitFilters,
    resetToDefault,
    applyView,
    saveView,
  } = useTableFilters({
    onSubmit: () => setFirst(0),
  });

  const metaType = useMemo(
    () => [
      { metaName: "origin", metaDisplay: "Origin", metaType: "string" },
      {
        metaName: "destination",
        metaDisplay: "Destination",
        metaType: "string",
      },
    ],
    []
  );

  //endregion

  //region Lane filtering and paging

  /**
   * Keeps only active lanes and then applies Origin/Destination filters.
   *
   * These are lightweight UI filters over the already loaded lane universe.
   */
  const filteredLanes = useMemo(() => {
    let data = (lanes || []).filter((lane) => isLaneActive(lane));

    for (const filter of metaPair) {
      const filterValue = String(filter?.value || "").toLowerCase().trim();
      if (!filterValue) continue;

      if (filter.name === "origin") {
        data = data.filter((lane) =>
          String(getLaneOrigin(lane) || "").toLowerCase().includes(filterValue)
        );
      }

      if (filter.name === "destination") {
        data = data.filter((lane) =>
          String(getLaneDestination(lane) || "")
            .toLowerCase()
            .includes(filterValue)
        );
      }
    }

    return data;
  }, [lanes, metaPair]);

  /**
   * Current page lane subset.
   * Row alternation is based on this paged collection.
   */
  const pagedLanes = useMemo(
    () => filteredLanes.slice(first, first + rows),
    [filteredLanes, first, rows]
  );

  //endregion

  //region Best value and suggestion helpers

  /**
   * Resolves the carrier display name of one quote.
   *
   * @param {object} quote - Quote DTO.
   * @returns {string} Carrier label.
   */
  const getCarrierName = useCallback(
    (quote) => {
      if (!quote) return "-";

      const carrierId = quote?.CarrierId ?? quote?.carrierId ?? null;
      if (carrierId != null) {
        const carrierName = carrierNameById.get(Number(carrierId));
        if (carrierName) return carrierName;
      }

      if (typeof quote?.Carrier === "string") return quote.Carrier;
      if (typeof quote?.carrier === "string") return quote.carrier;
      if (quote?.Carrier?.Name) return quote.Carrier.Name;
      if (quote?.carrier?.Name) return quote.carrier.Name;
      if (quote?.CarrierName) return quote.CarrierName;
      if (quote?.carrierName) return quote.carrierName;

      return "-";
    },
    [carrierNameById]
  );

  /**
   * Resolves whether one quote is suggested in the currently selected solution.
   *
   * @param {object} quote - Quote DTO.
   * @param {number|null} laneId - Lane id for optional lane-scoped fallback.
   * @returns {boolean} True when suggested.
   */
  const getQuoteSuggestedFromSolution = useCallback(
    (quote, laneId = null) => {
      if (selectedSolutionId == null) return false;

      const quoteId = getQuoteId(quote);

      if (
        quoteId != null &&
        selectedSolutionSuggestedQuoteIdSet.has(Number(quoteId))
      ) {
        return true;
      }

      if (
        selectedSolutionLaneAwardMap instanceof Map &&
        laneId != null &&
        selectedSolutionLaneAwardMap.has(Number(laneId))
      ) {
        const awards = selectedSolutionLaneAwardMap.get(Number(laneId)) || [];

        return awards.some((award) => {
          const awardQuoteId = getAwardQuoteId(award);
          return (
            awardQuoteId != null && Number(awardQuoteId) === Number(quoteId)
          );
        });
      }

      return false;
    },
    [
      selectedSolutionId,
      selectedSolutionSuggestedQuoteIdSet,
      selectedSolutionLaneAwardMap,
    ]
  );

  /**
   * Resolves the best baseline value for one lane.
   *
   * Priority:
   * 1) laneBestMap from the parent
   * 2) local fallback from currently visible lane quotes
   *
   * Notes:
   * - laneBestMap should already be prepared upstream with the correct semantics
   *   for the current table mode;
   * - the local fallback is only a safe fallback, not the preferred source.
   *
   * @param {number|null} laneId - Lane id.
   * @param {Array<object>} laneQuotes - Visible quotes for that lane.
   * @returns {number|null} Best value for the active metric.
   */
  const getBestValueForLane = useCallback(
    (laneId, laneQuotes) => {
      const normalizedLaneId = laneId != null ? Number(laneId) : null;

      const mapEntry =
        laneBestMap instanceof Map && Number.isFinite(normalizedLaneId)
          ? laneBestMap.get(normalizedLaneId)
          : null;

      if (metricKey === "unit") {
        const fromMap =
          mapEntry?.bestUnitPrice ??
          mapEntry?.bestBidUnitValue ??
          mapEntry?.minUnitPrice ??
          null;

        if (fromMap != null && Number.isFinite(Number(fromMap))) {
          return Number(fromMap);
        }

        const localValues = (laneQuotes || [])
          .map((quote) => getUnitPriceValue(quote))
          .filter((value) => value != null);

        return localValues.length ? Math.min(...localValues) : null;
      }

      if (metricKey === "bid") {
        const fromMap = mapEntry?.bestBidValue ?? mapEntry?.minBidValue ?? null;

        if (fromMap != null && Number.isFinite(Number(fromMap))) {
          return Number(fromMap);
        }

        const localValues = (laneQuotes || [])
          .map((quote) => getBidValue(quote))
          .filter((value) => value != null);

        return localValues.length ? Math.min(...localValues) : null;
      }

      if (metricKey === "loads") {
        const fromMap = mapEntry?.bestLoadsValue ?? mapEntry?.maxLoads ?? null;

        if (fromMap != null && Number.isFinite(Number(fromMap))) {
          return Number(fromMap);
        }

        const localValues = (laneQuotes || [])
          .map((quote) => getLoadsValue(quote))
          .filter((value) => value != null);

        return localValues.length ? Math.max(...localValues) : null;
      }

      if (metricKey === "transit") {
        const fromMap =
          mapEntry?.bestEtdValue ?? mapEntry?.minEstimatedTransitDays ?? null;

        if (fromMap != null && Number.isFinite(Number(fromMap))) {
          return Number(fromMap);
        }

        const localValues = (laneQuotes || [])
          .map((quote) => getTransitValue(quote))
          .filter((value) => value != null);

        return localValues.length ? Math.min(...localValues) : null;
      }

      return null;
    },
    [laneBestMap, metricKey]
  );

  /**
   * Resolves the active metric value for one quote.
   *
   * @param {object} quote - Quote DTO.
   * @returns {number|null} Active metric value.
   */
  const getActiveMetricValueForQuote = useCallback(
    (quote) => getQuoteMetricValue(metricKey, quote),
    [metricKey]
  );

  /**
   * Resolves the "Loads Remaining To Satisfy" value for one lane.
   *
   * Priority:
   * 1) laneRemainingLoadsByLaneId from the parent
   * 2) local fallback: tenderTargetLoads - sum(quote loads in the lane)
   *
   * Important:
   * - parent-provided value is preferred because it may come from summary logic;
   * - the local fallback exists only as a safe rendering fallback.
   *
   * @param {number|null} laneId - Lane id.
   * @param {Array<object>} laneQuotes - Visible lane quotes.
   * @returns {number|null} Remaining loads or null.
   */
  const getLoadsRemainingForLane = useCallback(
    (laneId, laneQuotes) => {
      const normalizedLaneId = laneId != null ? Number(laneId) : null;

      if (
        laneRemainingLoadsByLaneId instanceof Map &&
        Number.isFinite(normalizedLaneId) &&
        laneRemainingLoadsByLaneId.has(normalizedLaneId)
      ) {
        const value = laneRemainingLoadsByLaneId.get(normalizedLaneId);
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
      }

      const targetLoads = Number(tenderTargetLoads);
      if (!Number.isFinite(targetLoads)) return null;

      const laneTotalLoads = (laneQuotes || []).reduce((sum, quote) => {
        const value = getLoadsValue(quote);
        return value != null ? sum + value : sum;
      }, 0);

      return Math.max(0, targetLoads - laneTotalLoads);
    },
    [laneRemainingLoadsByLaneId, tenderTargetLoads]
  );

  /**
   * Resolves the background color of a quote metric cell.
   *
   * Important:
   * - rowBaseBackground is the fallback and no-data color;
   * - this preserves the white / grey-light alternation whenever no heat color applies.
   *
   * @param {object} args - Color resolution args.
   * @param {number|null} args.bestValue - Best lane value.
   * @param {number|null} args.activeMetricValue - Current quote metric value.
   * @param {string} args.rowBaseBackground - Base lane background.
   * @returns {string} Background color.
   */
  const getMetricCellBackground = useCallback(
    ({ bestValue, activeMetricValue, rowBaseBackground }) => {
      if (bestValue == null || activeMetricValue == null) {
        return rowBaseBackground;
      }

      if (metricKey === "loads") {
        return getCellColor(bestValue, activeMetricValue, {
          noData: rowBaseBackground,
          fallback: rowBaseBackground,
        });
      }

      return getCellColor(activeMetricValue, bestValue, {
        noData: rowBaseBackground,
        fallback: rowBaseBackground,
      });
    },
    [metricKey]
  );

  //endregion

  //region Accept toggle rules

  /**
   * A quote can be toggled only when:
   * - the tender is not finished;
   * - Spot: always;
   * - Non-Spot: only if the quote belongs to the last phase.
   *
   * Important:
   * - this rule is independent from currentPhaseId;
   * - currentPhaseId only controls the active solution context.
   */
  const canToggleQuote = useCallback(
    (quote) => {
      if (!quote) return false;
      if (isFinished) return false;
      if (isSpotTender) return true;

      if (getQuoteIsLastPhase(quote) === true) return true;

      const quotePhaseId = getQuotePhaseId(quote);
      if (quotePhaseId == null || lastPhaseId == null) return false;

      return Number(quotePhaseId) === Number(lastPhaseId);
    },
    [isFinished, isSpotTender, lastPhaseId]
  );

  /**
   * Busy-state helper that accepts either Set or array payloads.
   *
   * @param {number|null} quoteId - Quote id.
   * @returns {boolean} True when busy.
   */
  const isBusy = useCallback(
    (quoteId) => {
      if (!quoteId) return false;
      if (!acceptBusyIds) return false;
      if (acceptBusyIds instanceof Set) return acceptBusyIds.has(quoteId);
      if (Array.isArray(acceptBusyIds)) return acceptBusyIds.includes(quoteId);
      return false;
    },
    [acceptBusyIds]
  );

  /**
   * Delegates accept / unaccept to the parent.
   *
   * @param {object} args - Mutation args.
   * @param {number} args.quoteId - Quote id.
   * @param {boolean} args.nextAccepted - Next accepted flag.
   * @returns {void}
   */
  const setQuoteAccepted = useCallback(
    ({ quoteId, nextAccepted }) => {
      if (!quoteId) return;

      if (typeof onSetQuoteAccepted === "function") {
        onSetQuoteAccepted({ quoteId, nextAccepted });
      }
    },
    [onSetQuoteAccepted]
  );

  const acceptedDisabledReason = useMemo(() => {
    return (
      t(
        "Quote selection is only allowed in the last phase for Non-Spot tenders."
      ) ||
      "Quote selection is only allowed in the last phase for Non-Spot tenders."
    );
  }, [t]);

  //endregion

  //region Column configuration

  /**
   * Dynamic columns for the current context.
   *
   * Important:
   * - BestMetric uses the active metric;
   * - LoadsRemainingToSatisfy is a lane-level column;
   * - UnitPrice and BidValue are both now supported explicitly.
   */
  const allColumns = useMemo(() => {
    const columns = [
      "Origin",
      "Destination",
      "BestMetric",
      "LoadsRemainingToSatisfy",
      "UnitPrice",
      "BidValue",
      "Loads",
      "EstimatedTransitDays",
      "Carrier",
    ];

    if (shouldRenderSuggestedColumn) {
      columns.push("IsSuggested");
    }

    if (shouldRenderAcceptedColumn) {
      columns.push("Accepted");
    }

    return columns;
  }, [shouldRenderSuggestedColumn, shouldRenderAcceptedColumn]);

  const [selectedColumns, setSelectedColumns] = useState(allColumns);

  useEffect(() => {
    setSelectedColumns((previous) => {
      const kept = previous.filter((column) => allColumns.includes(column));
      const missing = allColumns.filter((column) => !kept.includes(column));
      return [...kept, ...missing];
    });
  }, [allColumns]);

  const bestMetricHeader = useMemo(
    () => getBestMetricHeader(metricKey, t),
    [metricKey, t]
  );

  //endregion

  //region Export

  /**
   * Exports the current filtered table.
   *
   * Notes:
   * - the exported Suggested value is derived from the selected solution;
   * - the exported quote source follows laneMode;
   * - this export is Tender-wide for quotes, matching the rendered table;
   * - lane quote order in the export matches the rendered metric-based order.
   */
  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("ViewLaneTable");

    const headerRow = [
      t("Origin"),
      t("Destination"),
      bestMetricHeader,
      t("LoadsRemainingToSatisfy") || "Loads Remaining To Satisfy",
      t("UnitPrice") || "Unit Price",
      t("BidValue") || "Bid Value",
      t("Loads"),
      t("EstimatedTransitDays") || "Estimated Transit Days",
      t("Carrier"),
    ];

    if (shouldRenderSuggestedColumn) {
      headerRow.push(t("IsSuggested") || "Is Suggested");
    }

    if (shouldRenderAcceptedColumn) {
      headerRow.push(t("Accepted") || "Accepted");
    }

    sheet.addRow(headerRow);

    filteredLanes.forEach((lane) => {
      const laneIdRaw = getLaneId(lane);
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) return;

      const origin = getLaneOrigin(lane) || "-";
      const destination = getLaneDestination(lane) || "-";
      const laneQuotes = quotesByLaneId.get(laneId) || [];
      const bestValue = getBestValueForLane(laneId, laneQuotes);
      const loadsRemaining = getLoadsRemainingForLane(laneId, laneQuotes);

      if (!laneQuotes.length) {
        const emptyRow = [
          origin,
          destination,
          formatMetricValue(bestValue, metricKey),
          formatPlainValue(loadsRemaining),
          "-",
          "-",
          "-",
          "-",
          "-",
        ];

        if (shouldRenderSuggestedColumn) {
          emptyRow.push("No");
        }

        if (shouldRenderAcceptedColumn) {
          emptyRow.push("No");
        }

        sheet.addRow(emptyRow);
        return;
      }

      laneQuotes.forEach((quote, index) => {
        const unitPrice = getUnitPriceValue(quote);
        const bidValue = getBidValue(quote);
        const loads = getLoadsValue(quote);
        const transit = getTransitValue(quote);
        const carrierName = getCarrierName(quote);
        const suggested = getQuoteSuggestedFromSolution(quote, laneId)
          ? "Yes"
          : "No";
        const accepted = getQuoteAccepted(quote) ? "Yes" : "No";

        const row = [
          index === 0 ? origin : "",
          index === 0 ? destination : "",
          index === 0 ? formatMetricValue(bestValue, metricKey) : "",
          index === 0 ? formatPlainValue(loadsRemaining) : "",
          formatCurrencyValue(unitPrice),
          formatCurrencyValue(bidValue),
          formatPlainValue(loads),
          formatPlainValue(transit),
          carrierName ?? "-",
        ];

        if (shouldRenderSuggestedColumn) {
          row.push(suggested);
        }

        if (shouldRenderAcceptedColumn) {
          row.push(accepted);
        }

        sheet.addRow(row);
      });
    });

    try {
      sheet.getRow(1).font = { bold: true };
    } catch {
      // Ignore styling issues during export.
    }

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${getExportFileSuffix(laneMode)}.xlsx`
    );
  };

  //endregion

  //region Render

  return (
    <div className="viewLaneTable-wrapper">
      <TableHeader
        filterCount={metaPair.length}
        onRefresh={onRefresh}
        onExport={exportExcel}
        onOpenViews={(event) => setAnchorV(event.currentTarget)}
        onOpenFilters={(event) => setAnchorAF(event.currentTarget)}
        onOpenColumns={(event) => setAnchorEls(event.currentTarget)}
      />

      <ColumnMenu
        anchorEl={anchorEls}
        open={!!anchorEls}
        onClose={() => setAnchorEls(null)}
        singleColumns={allColumns}
        selectedColumns={selectedColumns}
        onChangeSelected={setSelectedColumns}
        columnGroups={[]}
      />

      <ViewsMenu
        anchorEl={anchorV}
        open={!!anchorV}
        onClose={() => setAnchorV(null)}
        viewsMap={views}
        onSelectDefault={() => {
          resetToDefault();
          setAnchorV(null);
        }}
        onSelectView={(view) => {
          applyView(view);
          setAnchorV(null);
        }}
        onCreateNewViewClick={() => {
          setAnchorV(null);
          setAnchorAF(document.getElementById("advFilter"));
        }}
      />

      <AdvanceFilter
        metaPair={metaPair}
        metaType={metaType}
        onAddMetaPair={addMetaPair}
        onSubmitFilters={submitFilters}
        onDeleteFilter={deleteFilter}
        onDeleteAllFilters={deleteAllFilters}
        anchorAF={anchorAF}
        setAnchorAF={setAnchorAF}
        onSaveFilter={(viewName) => saveView(viewName)}
        views={views}
        onToasts={() => { }}
      />

      <div className="viewLaneTable-table-container">
        <table className="viewLaneTable">
          <thead>
            <tr>
              {selectedColumns.includes("Origin") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-origin"
                >
                  {t("Origin")}
                </th>
              )}

              {selectedColumns.includes("Destination") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-destination"
                >
                  {t("Destination")}
                </th>
              )}

              {selectedColumns.includes("BestMetric") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-best"
                >
                  {bestMetricHeader}
                </th>
              )}

              {selectedColumns.includes("LoadsRemainingToSatisfy") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-loads-remaining"
                >
                  {t("LoadsRemainingToSatisfy") ||
                    "Loads Remaining To Satisfy"}
                </th>
              )}

              {selectedColumns.includes("UnitPrice") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-unit-price"
                >
                  {t("UnitPrice") || "Unit Price"}
                </th>
              )}

              {selectedColumns.includes("BidValue") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-bid"
                >
                  {t("BidValue") || "Bid Value"}
                </th>
              )}

              {selectedColumns.includes("Loads") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-loads"
                >
                  {t("Loads")}
                </th>
              )}

              {selectedColumns.includes("EstimatedTransitDays") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-transit"
                >
                  {t("EstimatedTransitDays") || "Estimated Transit Days"}
                </th>
              )}

              {selectedColumns.includes("Carrier") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-carrier"
                >
                  {t("Carrier")}
                </th>
              )}

              {selectedColumns.includes("IsSuggested") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-suggested"
                >
                  {t("IsSuggested") || "Is Suggested"}
                </th>
              )}

              {selectedColumns.includes("Accepted") && (
                <th
                  style={headerCellStyle()}
                  className="viewLaneTable-header-cell col-accepted"
                >
                  {t("Accepted") || "Accepted"}
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {pagedLanes.map((lane, laneIndex) => {
              const laneIdRaw = getLaneId(lane);
              const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
              if (!Number.isFinite(laneId)) return null;

              const origin = getLaneOrigin(lane) || "-";
              const destination = getLaneDestination(lane) || "-";
              const rowBaseBackground = getLaneBaseBackground(laneIndex);
              const rowSubLineColor = getLaneSubLineColor(rowBaseBackground);

              /**
               * Base style shared by all cells in the current lane group.
               *
               * Important:
               * - the CSS variable controls the visible quote sub-line separator;
               * - white lane groups use grey-light separators;
               * - grey-light lane groups use white separators;
               * - keeping this as a CSS variable avoids duplicating border rules inline.
               *
               * @param {object} extra - Extra tdStyle options.
               * @returns {object} Cell style with lane-specific separator color.
               */
              const laneCellStyle = (extra = {}) =>
                tdStyle({
                  ...extra,
                  "--view-lane-subline-color": rowSubLineColor,
                });

              /**
               * Quotes for the lane are already sorted best -> worst according to
               * the current metric because quotesByLaneId is metric-aware.
               */
              const laneQuotes = quotesByLaneId.get(laneId) || [];
              const bestValue = getBestValueForLane(laneId, laneQuotes);
              const loadsRemaining = getLoadsRemainingForLane(laneId, laneQuotes);

              const bestBackground =
                bestValue != null
                  ? getCellColor(bestValue, bestValue, {
                    noData: rowBaseBackground,
                    fallback: rowBaseBackground,
                  })
                  : rowBaseBackground;

              /**
               * Empty lane row:
               * - renders the lane group even when there are no visible quotes;
               * - keeps the alternating lane base color;
               * - preserves normal lane boundaries because there are no internal
               *   quote separators in this case.
               */
              if (!laneQuotes.length) {
                return (
                  <tr key={`lane-${laneId}`}>
                    {selectedColumns.includes("Origin") && (
                      <td
                        className="viewLaneTable-cell col-origin"
                        style={laneCellStyle({
                          background: rowBaseBackground,
                          fontWeight: "bold",
                        })}
                      >
                        {origin}
                      </td>
                    )}

                    {selectedColumns.includes("Destination") && (
                      <td
                        className="viewLaneTable-cell col-destination"
                        style={laneCellStyle({
                          background: rowBaseBackground,
                          fontWeight: "bold",
                        })}
                      >
                        {destination}
                      </td>
                    )}

                    {selectedColumns.includes("BestMetric") && (
                      <td
                        className="viewLaneTable-cell col-best"
                        style={laneCellStyle({ background: bestBackground })}
                      >
                        {formatMetricValue(bestValue, metricKey)}
                      </td>
                    )}

                    {selectedColumns.includes("LoadsRemainingToSatisfy") && (
                      <td
                        className="viewLaneTable-cell col-loads-remaining"
                        style={laneCellStyle({ background: rowBaseBackground })}
                      >
                        {formatPlainValue(loadsRemaining)}
                      </td>
                    )}

                    {selectedColumns.includes("UnitPrice") && (
                      <td
                        className="viewLaneTable-cell col-unit-price"
                        style={laneCellStyle({ background: rowBaseBackground })}
                      >
                        -
                      </td>
                    )}

                    {selectedColumns.includes("BidValue") && (
                      <td
                        className="viewLaneTable-cell col-bid"
                        style={laneCellStyle({ background: rowBaseBackground })}
                      >
                        -
                      </td>
                    )}

                    {selectedColumns.includes("Loads") && (
                      <td
                        className="viewLaneTable-cell col-loads"
                        style={laneCellStyle({ background: rowBaseBackground })}
                      >
                        -
                      </td>
                    )}

                    {selectedColumns.includes("EstimatedTransitDays") && (
                      <td
                        className="viewLaneTable-cell col-transit"
                        style={laneCellStyle({ background: rowBaseBackground })}
                      >
                        -
                      </td>
                    )}

                    {selectedColumns.includes("Carrier") && (
                      <td
                        className="viewLaneTable-cell col-carrier"
                        style={laneCellStyle({ background: rowBaseBackground })}
                      >
                        -
                      </td>
                    )}

                    {selectedColumns.includes("IsSuggested") && (
                      <td
                        className="viewLaneTable-cell col-suggested"
                        style={laneCellStyle({ background: rowBaseBackground })}
                      >
                        <MCheckBox
                          label={null}
                          checked={false}
                          disabled={true}
                          noPadding={true}
                          fontSize={"22px"}
                          onChange={() => { }}
                        />
                      </td>
                    )}

                    {selectedColumns.includes("Accepted") && (
                      <td
                        className="viewLaneTable-cell col-accepted"
                        style={laneCellStyle({ background: rowBaseBackground })}
                      >
                        <MCheckBox
                          label={null}
                          checked={false}
                          disabled={true}
                          noPadding={true}
                          fontSize={"22px"}
                          onChange={() => { }}
                        />
                      </td>
                    )}
                  </tr>
                );
              }

              /**
               * Lane group with one or more quotes.
               *
               * The lane cells (Origin / Destination / BestMetric / LoadsRemaining)
               * use rowSpan so the user visually reads the lane as a grouped block.
               *
               * Important visual rule:
               * - the quote order stays metric-sorted inside the lane;
               * - only quote-level cells receive the thin internal border logic;
               * - the sub-line separator color is driven by rowSubLineColor;
               * - grey lane groups therefore get white quote separators;
               * - white lane groups keep grey-light quote separators.
               */
              return laneQuotes.map((quote, quoteIndex) => {
                const unitPrice = getUnitPriceValue(quote);
                const bidValue = getBidValue(quote);
                const loads = getLoadsValue(quote);
                const transit = getTransitValue(quote);
                const carrierName = getCarrierName(quote);
                const suggested = getQuoteSuggestedFromSolution(quote, laneId);
                const accepted = getQuoteAccepted(quote);

                const activeMetricValue = getActiveMetricValueForQuote(quote);

                const metricBackground = getMetricCellBackground({
                  bestValue,
                  activeMetricValue,
                  rowBaseBackground,
                });

                const quoteCellBorderStyle = getQuoteCellBorderStyle(
                  quoteIndex,
                  laneQuotes.length
                );

                const quoteId = getQuoteId(quote);
                const canToggle = canToggleQuote(quote);
                const disabled = !quoteId || !canToggle || isBusy(quoteId);

                const tooltip = isFinished
                  ? t("TenderFinished")
                  : !isSpotTender && !canToggle
                    ? acceptedDisabledReason
                    : "";

                return (
                  <tr key={`lane-${laneId}-quote-${quoteId ?? quoteIndex}`}>
                    {quoteIndex === 0 && selectedColumns.includes("Origin") && (
                      <td
                        rowSpan={laneQuotes.length}
                        className="viewLaneTable-cell col-origin"
                        style={laneCellStyle({
                          background: rowBaseBackground,
                          fontWeight: "bold",
                        })}
                      >
                        {origin}
                      </td>
                    )}

                    {quoteIndex === 0 && selectedColumns.includes("Destination") && (
                      <td
                        rowSpan={laneQuotes.length}
                        className="viewLaneTable-cell col-destination"
                        style={laneCellStyle({
                          background: rowBaseBackground,
                          fontWeight: "bold",
                        })}
                      >
                        {destination}
                      </td>
                    )}

                    {quoteIndex === 0 && selectedColumns.includes("BestMetric") && (
                      <td
                        rowSpan={laneQuotes.length}
                        className="viewLaneTable-cell col-best"
                        style={laneCellStyle({ background: bestBackground })}
                      >
                        {formatMetricValue(bestValue, metricKey)}
                      </td>
                    )}

                    {quoteIndex === 0 &&
                      selectedColumns.includes("LoadsRemainingToSatisfy") && (
                        <td
                          rowSpan={laneQuotes.length}
                          className="viewLaneTable-cell col-loads-remaining"
                          style={laneCellStyle({ background: rowBaseBackground })}
                        >
                          {formatPlainValue(loadsRemaining)}
                        </td>
                      )}

                    {selectedColumns.includes("UnitPrice") && (
                      <td
                        className="viewLaneTable-cell col-unit-price"
                        style={{
                          ...laneCellStyle({
                            background:
                              metricKey === "unit"
                                ? metricBackground
                                : rowBaseBackground,
                          }),
                          ...quoteCellBorderStyle,
                        }}
                      >
                        {formatCurrencyValue(unitPrice)}
                      </td>
                    )}

                    {selectedColumns.includes("BidValue") && (
                      <td
                        className="viewLaneTable-cell col-bid"
                        style={{
                          ...laneCellStyle({
                            background:
                              metricKey === "bid"
                                ? metricBackground
                                : rowBaseBackground,
                          }),
                          ...quoteCellBorderStyle,
                        }}
                      >
                        {formatCurrencyValue(bidValue)}
                      </td>
                    )}

                    {selectedColumns.includes("Loads") && (
                      <td
                        className="viewLaneTable-cell col-loads"
                        style={{
                          ...laneCellStyle({
                            background:
                              metricKey === "loads"
                                ? metricBackground
                                : rowBaseBackground,
                          }),
                          ...quoteCellBorderStyle,
                        }}
                      >
                        {formatPlainValue(loads)}
                      </td>
                    )}

                    {selectedColumns.includes("EstimatedTransitDays") && (
                      <td
                        className="viewLaneTable-cell col-transit"
                        style={{
                          ...laneCellStyle({
                            background:
                              metricKey === "transit"
                                ? metricBackground
                                : rowBaseBackground,
                          }),
                          ...quoteCellBorderStyle,
                        }}
                      >
                        {formatPlainValue(transit)}
                      </td>
                    )}

                    {selectedColumns.includes("Carrier") && (
                      <td
                        className="viewLaneTable-cell col-carrier"
                        style={{
                          ...laneCellStyle({ background: rowBaseBackground }),
                          ...quoteCellBorderStyle,
                        }}
                      >
                        {carrierName ?? "-"}
                      </td>
                    )}

                    {selectedColumns.includes("IsSuggested") && (
                      <td
                        className="viewLaneTable-cell col-suggested"
                        style={{
                          ...laneCellStyle({ background: rowBaseBackground }),
                          ...quoteCellBorderStyle,
                        }}
                      >
                        <MCheckBox
                          label={null}
                          checked={!!suggested}
                          disabled={true}
                          noPadding={true}
                          fontSize={"22px"}
                          onChange={() => { }}
                        />
                      </td>
                    )}

                    {selectedColumns.includes("Accepted") && (
                      <td
                        className="viewLaneTable-cell col-accepted"
                        style={{
                          ...laneCellStyle({ background: rowBaseBackground }),
                          ...quoteCellBorderStyle,
                        }}
                      >
                        <MCheckBox
                          label={null}
                          checked={!!accepted}
                          disabled={disabled}
                          tooltipName={tooltip}
                          noPadding={true}
                          fontSize={"22px"}
                          onChange={(event) => {
                            const nextAccepted = !!event?.target?.checked;
                            if (!quoteId) return;

                            setQuoteAccepted({ quoteId, nextAccepted });
                          }}
                        />
                      </td>
                    )}
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>

      <Paginator
        first={first}
        rows={rows}
        totalRecords={filteredLanes.length}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(event) => {
          setFirst(event.first);
          setRows(event.rows);
        }}
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      />
    </div>
  );

  //endregion
}

ViewLaneTable.propTypes = {
  tenderInfo: PropTypes.object,

  acceptedQuotes: PropTypes.array,
  allQuotes: PropTypes.array,

  lanes: PropTypes.array.isRequired,
  carriers: PropTypes.array.isRequired,

  onRefresh: PropTypes.func,
  metricKey: PropTypes.oneOf(["unit", "bid", "loads", "transit"]),
  laneMode: PropTypes.oneOf(["accepted", "total"]),

  isFinished: PropTypes.bool,
  isSpotTender: PropTypes.bool,

  /**
   * Context only:
   * - in Spot mode it is irrelevant;
   * - in Non-Spot mode it represents the global page phase currently used
   *   to load/select the active solution set;
   * - it must NOT be used to FE-filter this table quote collection.
   */
  currentPhaseId: PropTypes.number,

  /**
   * Multi-metric lane best map expected from the parent.
   *
   * Expected keys per lane can include:
   * - bestUnitPrice / bestBidUnitValue
   * - bestBidValue
   * - bestLoadsValue
   * - bestEtdValue
   */
  laneBestMap: PropTypes.instanceOf(Map),

  /**
   * Future-ready props for "Loads Remaining To Satisfy".
   */
  tenderTargetLoads: PropTypes.number,
  laneRemainingLoadsByLaneId: PropTypes.instanceOf(Map),

  acceptBusyIds: PropTypes.oneOfType([
    PropTypes.instanceOf(Set),
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ),
  ]),
  onSetQuoteAccepted: PropTypes.func,

  showAcceptedColumn: PropTypes.bool,
  showSuggestedColumn: PropTypes.bool,
  analysisSolutionReady: PropTypes.bool,

  selectedSolutionId: PropTypes.number,
  selectedSolution: PropTypes.object,
  selectedSolutionAwards: PropTypes.array,
  selectedSolutionSuggestedQuoteIds: PropTypes.arrayOf(PropTypes.number),
  selectedSolutionAwardsByLaneId: PropTypes.instanceOf(Map),
};